/* csvExport.js
   -----------
   Building and downloading CSV files in the browser.

   WHY THIS IS DONE CLIENT-SIDE

   The menu is already loaded in state. Asking the server to rebuild it means
   a round trip for data the page is holding, and it needs a GET endpoint that
   carries auth in the query string — which is how the original download links
   ended up broken: they passed bus_id but not key_secret, so the server
   rejected every one of them.

   TWO THINGS THAT GO WRONG WITH BROWSER-MADE CSV

   Excel assumes the system encoding unless the file starts with a UTF-8 byte
   order mark. Without it, ₹ and any Devanagari in an item name arrive as
   mojibake, and the owner concludes the export is broken.

   A cell beginning with =, +, - or @ is treated as a formula when the file is
   opened. A menu item genuinely called "=Special of the day" would execute,
   and a malicious one can reach out to a URL. Prefixing those cells with a
   single quote is the standard defence and is invisible in the spreadsheet.
*/

const FORMULA_STARTERS = ["=", "+", "-", "@", "\t", "\r"];

/** Escape one value for CSV, and neutralise anything Excel would run. */
const escapeCell = (value) => {
  if (value === null || value === undefined) return "";

  let text = String(value);

  // Excel runs a cell that opens with any of these. The leading apostrophe
  // marks it as text and does not show in the spreadsheet.
  if (text.length && FORMULA_STARTERS.includes(text[0])) {
    text = `'${text}`;
  }

  // Quotes, commas and newlines all need the field wrapped, and any quote
  // inside doubled.
  if (/[",\n\r]/.test(text)) {
    text = `"${text.replace(/"/g, '""')}"`;
  }

  return text;
};

/** Turn a header row plus data rows into CSV text. */
export const toCsv = (headers, rows) => {
  const lines = [headers.map(escapeCell).join(",")];
  rows.forEach((row) => lines.push(row.map(escapeCell).join(",")));
  // CRLF because Excel on Windows still handles it more predictably than LF.
  return lines.join("\r\n");
};

/**
 * Trigger a download of CSV text.
 *
 * The \ufeff prefix is the byte order mark. Excel needs it to read the file as
 * UTF-8; without it, rupee symbols and Hindi text arrive corrupted.
 */
export const downloadCsv = (filename, csvText) => {
  const blob = new Blob([`\ufeff${csvText}`], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Released on the next tick rather than immediately — Safari cancels the
  // download if the object URL is revoked before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Today as 2026-08-20, for filenames that sort correctly in a folder. */
export const todayStamp = () => new Date().toISOString().slice(0, 10);